import { useEffect, useState } from 'react';
import { upsertOrganizationContext } from './isoImplementationApi';
import type {
  OrganizationItemContextView,
  TaskWithProgressView,
  UpsertOrganizationContextPayload,
} from './types';
import { showToast } from '../../../components/ui/toast.utils';

const APPLICABILITY_OPTIONS = [
  { value: 'sin_definir', label: 'Sin definir' },
  { value: 'aplica', label: 'Aplica' },
  { value: 'no_aplica', label: 'No aplica' },
] as const;

interface IsoGuideOrgContextPanelProps {
  task: TaskWithProgressView;
  isSuperadmin: boolean;
  onUpdated: (context: OrganizationItemContextView) => void;
}

export function IsoGuideOrgContextPanel({
  task,
  isSuperadmin,
  onUpdated,
}: IsoGuideOrgContextPanelProps) {
  const ctx = task.organizationContext;
  const [saving, setSaving] = useState(false);
  const [meaning, setMeaning] = useState(ctx?.meaningForOrganization ?? '');
  const [particulars, setParticulars] = useState(ctx?.implementationParticulars ?? '');
  const [applicability, setApplicability] = useState(ctx?.applicability ?? 'sin_definir');
  const [justification, setJustification] = useState(ctx?.applicabilityJustification ?? '');

  useEffect(() => {
    setMeaning(ctx?.meaningForOrganization ?? '');
    setParticulars(ctx?.implementationParticulars ?? '');
    setApplicability(ctx?.applicability ?? 'sin_definir');
    setJustification(ctx?.applicabilityJustification ?? '');
  }, [ctx, task.id]);

  const saveContext = async () => {
    if (applicability === 'no_aplica' && !justification.trim()) {
      showToast('Ingresá una justificación para marcar este requisito como No aplica.', 'warning');
      return;
    }

    const payload: UpsertOrganizationContextPayload = {
      applicability,
      applicabilityJustification:
        applicability === 'no_aplica' ? justification.trim() : null,
      meaningForOrganization: meaning.trim() || null,
      implementationParticulars: particulars.trim() || null,
    };

    setSaving(true);
    try {
      const updated = await upsertOrganizationContext(task.id, payload);
      onUpdated(updated);
      showToast('Contexto de la empresa guardado.', 'success');
    } finally {
      setSaving(false);
    }
  };

  const readonlyTooltip =
    'Editable solo por consultor Microsyst (superadmin)';

  return (
    <aside className="space-y-4 rounded-lg border border-border bg-muted/30 p-4">
      <h3 className="text-sm font-semibold">Contexto de esta empresa</h3>

      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Aplicabilidad</label>
        {isSuperadmin ? (
          <select
            className="input w-full text-sm"
            value={applicability}
            disabled={saving}
            onChange={(e) => {
              const next = e.target.value as OrganizationItemContextView['applicability'];
              setApplicability(next);
            }}
          >
            {APPLICABILITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-sm" title={readonlyTooltip}>
            {APPLICABILITY_OPTIONS.find((o) => o.value === applicability)?.label ?? '—'}
          </p>
        )}
      </div>

      {applicability === 'no_aplica' ? (
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Justificación</label>
          {isSuperadmin ? (
            <textarea
              className="input min-h-[4rem] w-full text-sm"
              value={justification}
              disabled={saving}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Explicá por qué este requisito no aplica a la organización…"
            />
          ) : (
            <p className="text-sm text-muted-foreground">{justification || '—'}</p>
          )}
        </div>
      ) : null}

      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground" title={readonlyTooltip}>
          Significado para esta empresa
        </label>
        {isSuperadmin ? (
          <textarea
            className="input min-h-[5rem] w-full text-sm"
            value={meaning}
            disabled={saving}
            onChange={(e) => setMeaning(e.target.value)}
            placeholder="Qué significa este requisito en este tenant…"
          />
        ) : (
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {meaning || '—'}
          </p>
        )}
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground" title={readonlyTooltip}>
          Particularidades de implementación
        </label>
        {isSuperadmin ? (
          <textarea
            className="input min-h-[5rem] w-full text-sm"
            value={particulars}
            disabled={saving}
            onChange={(e) => setParticulars(e.target.value)}
            placeholder="Cómo se implementará aquí…"
          />
        ) : (
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {particulars || '—'}
          </p>
        )}
      </div>

      {!isSuperadmin ? (
        <p className="text-xs text-muted-foreground italic">{readonlyTooltip}</p>
      ) : (
        <button
          type="button"
          className="btn btn-primary w-full"
          disabled={saving}
          onClick={() => void saveContext()}
        >
          {saving ? 'Guardando…' : 'Guardar contexto'}
        </button>
      )}
    </aside>
  );
}

interface IsoGuideRequirementCardProps {
  task: TaskWithProgressView;
  isSelected: boolean;
  onSelect: () => void;
}

function HtmlBlock({ html }: { html: string }) {
  return (
    <div
      className="prose prose-sm max-w-none text-sm leading-relaxed [&_strong]:font-semibold"
      // Contenido seed controlado desde docs/ISO (sin input de usuario)
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export function IsoGuideRequirementCard({
  task,
  isSelected,
  onSelect,
}: IsoGuideRequirementCardProps) {
  const [open, setOpen] = useState(false);
  const blocks = task.contentBlocks;
  const docType = task.docType ?? task.tags[0] ?? '';

  return (
    <article
      id={`req-${task.externalId ?? task.id}`}
      className={`scroll-mt-4 overflow-hidden rounded-lg border bg-card ${
        isSelected ? 'border-primary ring-1 ring-primary/30' : 'border-border'
      }`}
      onMouseEnter={onSelect}
      onFocus={onSelect}
    >
      <button
        type="button"
        className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-muted/40"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="font-mono text-sm font-semibold text-emerald-800 shrink-0">
          {task.externalId ?? task.clauseRef}
        </span>
        <span className="flex-1 font-medium text-sm">{task.title}</span>
        {docType ? (
          <span
            className={`shrink-0 rounded px-2 py-0.5 font-mono text-[10px] uppercase ${
              docType === 'obligatorio'
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {docType}
          </span>
        ) : null}
        <span className="text-muted-foreground text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && blocks ? (
        <div className="border-t border-border px-4 pb-4 pt-2 space-y-4">
          {blocks.exige ? (
            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Qué exige la norma
              </h4>
              <p className="text-sm leading-relaxed">{blocks.exige}</p>
            </section>
          ) : null}
          {blocks.porque ? (
            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Por qué importa
              </h4>
              <p className="text-sm leading-relaxed">{blocks.porque}</p>
            </section>
          ) : null}
          {(blocks.pasos ?? []).length > 0 ? (
            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Cómo implementarlo
              </h4>
              <ol className="list-none space-y-2 pl-0">
                {(blocks.pasos ?? []).map((paso, idx) => (
                  <li key={idx} className="relative pl-8 text-sm">
                    <span className="absolute left-0 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-700 text-xs font-mono text-white">
                      {idx + 1}
                    </span>
                    <HtmlBlock html={paso} />
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
          {blocks.entregable ? (
            <section className="rounded-md bg-emerald-50 p-3 text-sm">
              <strong className="text-emerald-800">Entregable: </strong>
              {blocks.entregable}
            </section>
          ) : null}
          {blocks.errorComun ? (
            <section className="border-l-2 border-orange-600 bg-orange-50/50 px-3 py-2 text-sm">
              <strong className="text-orange-800">Error frecuente: </strong>
              {blocks.errorComun}
            </section>
          ) : null}
          {(blocks.terms ?? []).length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {(blocks.terms ?? []).map((term) => (
                <span
                  key={term}
                  className="rounded bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground"
                >
                  {term}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
